import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { type Platforms } from 'shared/api/accounts';
import { type AttributionResp } from 'shared/api/attributionModels';
import { type IntegrationAttributionResp as BOIntegratedAttributionResp } from 'shared/api/budgetOptimiser';
import { type ChannelGroupingResp } from 'shared/api/channelGroupings';
import { type ConnectorResp, type ConnectorsResp } from 'shared/api/connectors';
import { type EnhancedSignalsResp } from 'shared/api/biddingSignals';
import { type StandardReportResp, type StandardReportsResp } from 'shared/api/standardReports';
import { useCurrentClient } from 'shared/context/ClientContext';
import logger from 'shared/logger';
import { capitalize } from '../utils/stringFormatter';
import { getPlatformDetailsByPlatform } from 'src/dataSource/utils';
import { queryKeys as attributionModelQueryKeys } from 'src/attributionModelV2/api';
import { queryKeys as budgetOptimiserQueryKeys } from 'src/budgetOptimiserV2/api';
import { queryKeys as channelGroupingQueryKeys } from 'src/channelGrouping/api';
import { queryKeys as connectorsQueryKeys } from 'src/connectors/api';
import { queryKeys as biddingSignalsQueryKeys } from 'src/biddingSignals/api';
import { queryKeys as reportingSolutionsQueryKeys } from 'reportingSolution/api';
import { queryKeys as creativeReportingQueryKeys } from 'src/creativeReporting/api';
import { usePathname, useRouteParams } from 'shared/hooks/useRouterPath';
import { type DashboardResp } from 'shared/api/dashboards';
import { type ProjectResp } from 'shared/api/projects';
import { type Creative } from 'shared/api/creativeAssets';
import { queryKeys as creativeAssetsQueryKeys } from 'src/creativeAssets/api';
const log = logger().child({
  namespace: `${__dirname} ${__filename}`
});
interface BreadcrumbPathProps {
  label: string;
  path: string;
  disabled?: boolean;
}
export const useBreadcrumbsPathProps = () => {
  const $ = _c(12);
  const {
    pathnameWithQueryParams,
    route
  } = usePathname();
  const params = useRouteParams();
  let t0;
  if ($[0] !== pathnameWithQueryParams) {
    t0 = getSegments(pathnameWithQueryParams);
    $[0] = pathnameWithQueryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const pathSegments = t0;
  let t1;
  if ($[2] !== route) {
    t1 = getSegments(route);
    $[2] = route;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const routeSegments = t1;
  const {
    currentClient
  } = useCurrentClient();
  const queryClient = useQueryClient();
  let t2;
  bb0: {
    if (pathSegments.length === 0) {
      let t3;
      if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = [{
          label: "Home",
          path: "/"
        }];
        $[4] = t3;
      } else {
        t3 = $[4];
      }
      t2 = t3;
      break bb0;
    }
    const segmentsParser = MODULE_SEGMENTS_PARSERS[pathSegments?.[0]] ?? defaultModuleSegmentsParser;
    let t3;
    if ($[5] !== currentClient || $[6] !== params || $[7] !== pathSegments || $[8] !== queryClient || $[9] !== routeSegments || $[10] !== segmentsParser) {
      t3 = segmentsParser(pathSegments, routeSegments, queryClient, currentClient, false, params);
      $[5] = currentClient;
      $[6] = params;
      $[7] = pathSegments;
      $[8] = queryClient;
      $[9] = routeSegments;
      $[10] = segmentsParser;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = t3;
  }
  const parsedSegments = t2;
  return parsedSegments;
};
export const getSegments = (path: string): string[] => {
  const pathArray = path.split('?');
  const segments = pathArray.length > 0 ? pathArray[0].split('/') : [];
  return segments.filter(segment => segment !== '');
};
const isQuery = (segment: string): boolean => {
  return /\[\w+]/.exec(segment) !== null;
};
type RouteSegmentsParser = (
/**
 * The segments of the path that are actually rendered in the URL.
 */
segments: string[],
/**
 * The segments of the path as NextJS sees them.
 * I.e. it renders the dynamic segments as /example/[xx_id]/edit.
 */
routeSegments: string[], queryClient: ReturnType<typeof useQueryClient>, currentClient: ReturnType<typeof useCurrentClient>['currentClient'], disableQueryParams?: boolean, params?: ReturnType<typeof useRouteParams>) => BreadcrumbPathProps[];
/**
 * Used as a default if no module specific parser is found
 */
const defaultModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disableQueryParams, params) => {
  let path = '';
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    const label = capitalize(segment).replace(/-/g, ' ');
    if (isQuery(routeSegments[index]) && disableQueryParams !== true) {
      log.warn(`Breadcrumb label for ${segment} is a dynamic route parameter. Please add a custom parser for this module.`);
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: disableQueryParams === true && isQuery(routeSegments[index])
    });
  });
  return breadcrumbPathProps;
};
const connectorsModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[connector_id]' || params?.connector_id === segment) {
      const connectorId = parseInt(segment);
      let connectorName: string | undefined = (queryClient.getQueryData([connectorsQueryKeys.clientConnectors, currentClient.id, connectorId]) as ConnectorResp[])?.[0]?.job;
      connectorName ??= (queryClient.getQueryData([connectorsQueryKeys.clientConnectors, currentClient.id]) as ConnectorsResp)?.find(connector => connector.configId === connectorId)?.job;
      label = connectorName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path
    });
  });
  return breadcrumbPathProps;
};
const reportingSolutionsModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[reporting_solution_id]' || params?.reporting_solution_id === segment) {
      const reportingSolutionId = parseInt(segment);
      let reportingSolutionName: string | undefined = (queryClient.getQueryData([reportingSolutionsQueryKeys.reportingSolutionsConfigs, currentClient.id, reportingSolutionId]) as StandardReportResp)?.name;
      reportingSolutionName ??= (queryClient.getQueryData([reportingSolutionsQueryKeys.reportingSolutionsConfigs, currentClient.id]) as StandardReportsResp)?.find(reportingSolution => reportingSolution.configId === reportingSolutionId)?.name;
      label = reportingSolutionName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path
    });
  });
  return breadcrumbPathProps;
};
const attributionModelsModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[model_id]' || params?.model_id === segment) {
      const attributionModelId = parseInt(segment);
      let attributionModelName: string | undefined = (queryClient.getQueryData([attributionModelQueryKeys.integrationAttribution, attributionModelId, currentClient.id]) as AttributionResp)?.name;
      attributionModelName ??= (queryClient.getQueryData([attributionModelQueryKeys.integrationAttribution, currentClient.id]) as AttributionResp[])?.find(attributionModel => attributionModel.id === attributionModelId)?.name;
      label = attributionModelName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: routeSegments[index] === '[model_id]' || params?.model_id === segment
    });
  });
  return breadcrumbPathProps;
};
const budgetOptimiserModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[budget_optimiser_model_id]' || params?.budget_optimiser_model_id === segment) {
      const budgetOptimiserId = parseInt(segment);
      let budgetOptimiserName: string | undefined = (queryClient.getQueryData([budgetOptimiserQueryKeys.budgetOptimiser, currentClient.id, budgetOptimiserId]) as BOIntegratedAttributionResp)?.name;
      budgetOptimiserName ??= (queryClient.getQueryData([budgetOptimiserQueryKeys.budgetOptimiser, currentClient.id]) as BOIntegratedAttributionResp[])?.find(budgetOptimiser => budgetOptimiser.id === budgetOptimiserId)?.name;
      label = budgetOptimiserName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: routeSegments[index] === '[budget_optimiser_model_id]' || params?.budget_optimiser_model_id === segment
    });
  });
  return breadcrumbPathProps;
};
const customGroupingModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[custom_grouping_id]' || params?.custom_grouping_id === segment) {
      const channelGroupingId = parseInt(segment);
      let channelGroupingName: string | undefined = (queryClient.getQueryData([channelGroupingQueryKeys.channelGrouping, channelGroupingId, currentClient.id]) as ChannelGroupingResp)?.name;
      channelGroupingName ??= (queryClient.getQueryData([channelGroupingQueryKeys.channelGroupings, channelGroupingId, currentClient.id]) as ChannelGroupingResp)?.name;
      label = channelGroupingName ?? '...';
    }
    if (routeSegments[index] === '[tab_id]' || params?.tab_id === segment || routeSegments[index] === '[data_source_tab_id]' || params?.data_source_tab_id === segment) {
      return;
    }
    if ((routeSegments.includes('[data_source_tab_id]') || params?.data_source_tab_id != null) && segment === 'data-sources') {
      return;
    }
    breadcrumbPathProps.push({
      label: routeSegments[index] === '[data_source_id]' || params?.data_source_id === segment ? getPlatformDetailsByPlatform(segment as Platforms)?.label ?? label : label,
      path
    });
  });
  return breadcrumbPathProps;
};
const biddingSignalsModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[bidding_signals_id]' || params?.bidding_signals_id === segment) {
      const biddingSignalsId = parseInt(segment);
      let biddingSignalsName: string | undefined = (queryClient.getQueryData([biddingSignalsQueryKeys.enhancedSignalsConfigs, currentClient.id, biddingSignalsId]) as EnhancedSignalsResp)?.configName;
      biddingSignalsName ??= (queryClient.getQueryData([biddingSignalsQueryKeys.enhancedSignalsConfigs, currentClient.id]) as EnhancedSignalsResp[])?.find(enhancedSignals => enhancedSignals.id === biddingSignalsId)?.configName;
      label = biddingSignalsName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: routeSegments[index] === '[bidding_signals_id]' || params?.bidding_signals_id === segment
    });
  });
  return breadcrumbPathProps;
};
const creativeReportingModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replace(/-/g, ' ');
    if (routeSegments[index] === '[dashboard_id]' || params?.dashboard_id === segment) {
      const dashboardId = parseInt(segment);
      let dashboardName = (queryClient.getQueryData([creativeReportingQueryKeys.dashboards, currentClient.id, dashboardId]) as DashboardResp | undefined)?.title;
      dashboardName ??= (queryClient.getQueryData([creativeReportingQueryKeys.dashboards, currentClient.id]) as DashboardResp[])?.find(dashboard => dashboard.id === dashboardId)?.title;
      label = dashboardName ?? '...';
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: routeSegments[index] === '[dashboard_id]' || params?.dashboard_id === segment
    });
  });
  return breadcrumbPathProps;
};
const creativeAssetsModuleSegmentsParser: RouteSegmentsParser = (segments, routeSegments, queryClient, currentClient, disabled, params) => {
  const breadcrumbPathProps: BreadcrumbPathProps[] = [];
  let path = '';
  segments.forEach((segment, index) => {
    path += `/${segment}`;
    let label = capitalize(segment).replaceAll('-', ' ');
    if (routeSegments[index] === '[project_id]' || params?.project_id === segment) {
      const projectId = segment;
      let projectName: string | undefined = (queryClient.getQueryData([creativeAssetsQueryKeys.projects, currentClient.id, projectId]) as ProjectResp | undefined)?.title;
      projectName ??= (queryClient.getQueryData([creativeAssetsQueryKeys.projects, currentClient.id]) as ProjectResp[])?.find(project => project.id.toString() === projectId)?.title;
      label = projectName ?? '...';
    }
    if (routeSegments[index] === '[asset_id]' || params?.asset_id === segment) {
      const assetId = segment;
      // Find project_id - it should be the segment after 'creative-assets'
      const creativeAssetsIndex = segments.indexOf('creative-assets');
      const projectId = creativeAssetsIndex >= 0 && creativeAssetsIndex + 1 < segments.length ? segments[creativeAssetsIndex + 1] : undefined;
      if (projectId != null) {
        const creativeName: string | undefined = (queryClient.getQueryData([creativeAssetsQueryKeys.creativeAssets, currentClient.id, projectId]) as Creative[])?.find(creative => creative.id === assetId)?.name;
        label = creativeName ?? '...';
      }
    }
    if (routeSegments[index] === '[tab_id]' || params?.tab_id === segment) {
      return;
    }
    breadcrumbPathProps.push({
      label,
      path,
      disabled: routeSegments[index] === '[project_id]' || params?.project_id === segment || routeSegments[index] === '[asset_id]' || params?.asset_id === segment
    });
  });
  return breadcrumbPathProps;
};
const MODULE_SEGMENTS_PARSERS: Record<string, RouteSegmentsParser> = {
  connectors: connectorsModuleSegmentsParser,
  'reporting-solutions': reportingSolutionsModuleSegmentsParser,
  'attribution-models': attributionModelsModuleSegmentsParser,
  'budget-optimiser': budgetOptimiserModuleSegmentsParser,
  'custom-groupings': customGroupingModuleSegmentsParser,
  'bidding-signals': biddingSignalsModuleSegmentsParser,
  'creative-reporting': creativeReportingModuleSegmentsParser,
  'creative-assets': creativeAssetsModuleSegmentsParser
};